import _ from 'lodash'
import type {ExtendedRefInfo} from '@wix/import-export-utils'
import {ReportableError} from '@wix/document-manager-utils'
import {
    getRefHostTemplateId,
    isReferredId,
    refCompDelimiter,
    splitReferredId
} from '../../../../../utils/inflationUtils'

interface ProbeIdStage {
    probeIds: string[]
}

const buildProbeIdStages = (probeIdGroups: string[][]): ProbeIdStage[] => {
    const seen = new Set<string>()
    return probeIdGroups
        .map(probeIds => ({
            probeIds: probeIds.filter(probeId => {
                if (seen.has(probeId)) {
                    return false
                }
                seen.add(probeId)
                return true
            })
        }))
        .filter(({probeIds}) => probeIds.length > 0)
}

export const getProbeIdStagesForPolymorphicReferencedMap = (refId: string): ProbeIdStage[] => {
    if (!isReferredId(refId)) {
        return [{probeIds: [refId]}]
    }
    const segments = splitReferredId(refId)
    const tailSuffixes = _.times(segments.length - 1, i => segments.slice(i + 1).join(refCompDelimiter))
    const directHost = getRefHostTemplateId(refId)
    const outerHost = segments[0]
    return buildProbeIdStages([[refId], tailSuffixes, [directHost, outerHost]])
}

export const getProbeIdCandidatesForPolymorphicReferencedMap = (refId: string): string[] =>
    _.flatMap(getProbeIdStagesForPolymorphicReferencedMap(refId), 'probeIds')

interface ResolveEffectiveReferencedMapArgs {
    refId: string
    namespace: string
    itemType: string
    refFieldInfo: ExtendedRefInfo
    hasReferenceInMap(referencedMap: string): boolean
    hasItemInMap(referencedMap: string, probeId: string): boolean
    probeIdStages: ProbeIdStage[]
}

export const resolveEffectiveReferencedMap = ({
    refId,
    namespace,
    itemType,
    refFieldInfo,
    hasReferenceInMap,
    hasItemInMap,
    probeIdStages
}: ResolveEffectiveReferencedMapArgs): string => {
    const {referencedMap, candidateMaps, mustResolveExactlyOne} = refFieldInfo

    if (!mustResolveExactlyOne || !candidateMaps || candidateMaps.length <= 1) {
        return referencedMap
    }

    const hintMap = candidateMaps.find(m => hasReferenceInMap(m))
    if (hintMap) {
        return hintMap
    }

    const attemptedProbeIds = _.flatMap(probeIdStages, 'probeIds')
    const probedIdsSummary = `probed ids: ${attemptedProbeIds.join(', ')}`
    for (const {probeIds} of probeIdStages) {
        const existingMaps = candidateMaps.filter(candidateMap =>
            probeIds.some(probeId => hasItemInMap(candidateMap, probeId))
        )
        if (existingMaps.length === 1) {
            return existingMaps[0]
        }

        if (existingMaps.length > 1) {
            throw new ReportableError({
                errorType: 'POLYMORPHIC_REFERENCED_MAP_AMBIGUOUS',
                message:
                    `VariantRelation.from ref "${refId}" in ${namespace}.${itemType}` +
                    ` exists in multiple candidate maps: ${existingMaps.join(', ')}` +
                    ` (${probedIdsSummary})`
            })
        }
    }

    throw new ReportableError({
        errorType: 'POLYMORPHIC_REFERENCED_MAP_UNRESOLVED',
        message:
            `VariantRelation.from ref "${refId}" in ${namespace}.${itemType}` +
            ` could not be resolved in any candidate map: ${candidateMaps.join(', ')}` +
            ` (${probedIdsSummary})`
    })
}
